import EditAccessGroupGeneral from './pages/General';
import EditAccessGroupUsers from './pages/Users';
import { AccessGroupResourcesPage } from './pages/Resources';
import {
  DNS_ACLS_DOCUMENT,
  DNS_REMOTES_DOCUMENT,
  GET_ACCESS_GROUPS,
  GET_DNS_ZONES,
  GET_L7_RESOURCES,
  GET_MONITORING_OBJECTS,
  GET_PROTECTED_PROFILES,
} from '@/common/actions/get';
import { AccessGroupResourceType, accessGroupTabs, resourceTabs } from '../../store';
import { OrderTypes } from '@/constants';
import { useQuery } from '@apollo/client';
import { Box, Typography } from '@servicepipe/ui-components';
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const ALL_RESOURCES_LIMIT = 3000;

const getL7AccountQueryVariables = (accountId) => ({
  page: 1,
  limit: ALL_RESOURCES_LIMIT,
  partnerClientAccountId: Number(accountId),
  deletedAt: null,
});

const getDnsZonesAccountQueryVariables = (accountId) => ({
  page: 1,
  limit: ALL_RESOURCES_LIMIT,
  partnerClientAccountId: Number(accountId),
  deletedAtEq: null,
});

const getDnsListAccountQueryVariables = (accountId) => ({
  page: 1,
  limit: ALL_RESOURCES_LIMIT,
  partnerClientAccountId: [Number(accountId)],
  deletedAtEq: null,
  sortId: OrderTypes.Desc,
});

export const EditAccessGroup = () => {
  const { id: accountId, groupId, accessTab } = useParams();
  const [searchParams] = useSearchParams();
  const resourceTab = searchParams.get('resourceTab');

  const { data, loading, refetch } = useQuery(GET_ACCESS_GROUPS, {
    variables: {
      filterId: Number(groupId),
      filterPartnerClientAccountId: Number(accountId),
      limit: 1,
      page: 1,
      deletedAt: null,
    },
    skip: !accountId || !groupId,
    fetchPolicy: 'network-only',
  });

  const accessGroup = data?.accessGroups?.items?.[0] ?? null;

  const renderContent = () => {
    if (accessTab === accessGroupTabs.users) {
      return <EditAccessGroupUsers accessGroup={accessGroup} refetch={refetch} />;
    }

    if (accessTab === accessGroupTabs.networkProtection) {
      const selected = resourceTab || resourceTabs.monitoringObjects;

      if (selected === resourceTabs.protectedProfiles) {
        return (
          <AccessGroupResourcesPage
            key={AccessGroupResourceType.L3ProtectedProfile}
            title="Protected profiles"
            addButtonLabel="Add protected profile"
            resourceType={AccessGroupResourceType.L3ProtectedProfile}
            filterRemap="filterL3ProtectedProfileName"
            accountQuery={GET_PROTECTED_PROFILES}
            accountItemsPath="l3ProtectedProfiles"
            getItemPrimary={(item) => item.profileName || item.name}
            getItemSecondary={(item) => item.name}
            innerTabs={[
              { key: resourceTabs.monitoringObjects, title: 'Monitoring objects' },
              { key: resourceTabs.protectedProfiles, title: 'Protected profiles' },
            ]}
            defaultResourceTab={resourceTabs.protectedProfiles}
          />
        );
      }

      return (
        <AccessGroupResourcesPage
          key={AccessGroupResourceType.L3MonitoringObject}
          title="Monitoring objects"
          addButtonLabel="Add monitoring object"
          resourceType={AccessGroupResourceType.L3MonitoringObject}
          accountQuery={GET_MONITORING_OBJECTS}
          accountItemsPath="l3MonitoringObjects"
          getItemPrimary={(item) => item.monitoringObjectName || item.name}
          getItemSecondary={(item) => item.name}
          innerTabs={[
            { key: resourceTabs.monitoringObjects, title: 'Monitoring objects' },
            { key: resourceTabs.protectedProfiles, title: 'Protected profiles' },
          ]}
          defaultResourceTab={resourceTabs.monitoringObjects}
        />
      );
    }

    if (accessTab === accessGroupTabs.applicationProtection) {
      return (
        <AccessGroupResourcesPage
          key={AccessGroupResourceType.L7Resource}
          title="Application protection"
          addButtonLabel="Add resource"
          resourceType={AccessGroupResourceType.L7Resource}
          filterRemap="filterL7ResourceName"
          accountQuery={GET_L7_RESOURCES}
          accountItemsPath="l7Resources"
          accountQueryVariables={getL7AccountQueryVariables(accountId)}
          getItemId={(item) => item.id}
          getItemPrimary={(item) => item.l7ResourceName || item.name}
          getItemSecondary={(item) => item.protectedIp}
        />
      );
    }

    if (accessTab === accessGroupTabs.dnsHosting) {
      const selected = resourceTab || resourceTabs.ipAddressGroups;

      if (selected === resourceTabs.dnsZones) {
        return (
          <AccessGroupResourcesPage
            key={AccessGroupResourceType.DnsResource}
            title="Dns zones"
            addButtonLabel="Add DNS zone"
            resourceType={AccessGroupResourceType.DnsResource}
            filterRemap="filterResourceId"
            accountQuery={GET_DNS_ZONES}
            accountItemsPath="dnsResources"
            accountQueryVariables={getDnsZonesAccountQueryVariables(accountId)}
            getItemId={(item) => item.id}
            getItemPrimary={(item) => item.zone}
            getItemSecondary={(item) => item.id}
            innerTabs={[
              { key: resourceTabs.ipAddressGroups, title: 'IP address groups' },
              { key: resourceTabs.dnsZones, title: 'Dns zones' },
              { key: resourceTabs.acls, title: 'ACLs' },
            ]}
            defaultResourceTab={resourceTabs.dnsZones}
          />
        );
      }

      if (selected === resourceTabs.acls) {
        return (
          <AccessGroupResourcesPage
            key={AccessGroupResourceType.DnsAcl}
            title="ACLs"
            addButtonLabel="Add ACL"
            resourceType={AccessGroupResourceType.DnsAcl}
            filterRemap="filterDnsAclName"
            accountQuery={DNS_ACLS_DOCUMENT}
            accountItemsPath="dnsAcls"
            extraAccountVariables={{ partnerClientAccountId: [Number(accountId)] }}
            accountQueryVariables={getDnsListAccountQueryVariables(accountId)}
            getItemId={(item) => item.id}
            getItemPrimary={(item) => item.name}
            getItemSecondary={(item) => item.id}
            innerTabs={[
              { key: resourceTabs.ipAddressGroups, title: 'IP address groups' },
              { key: resourceTabs.dnsZones, title: 'Dns zones' },
              { key: resourceTabs.acls, title: 'ACLs' },
            ]}
            defaultResourceTab={resourceTabs.acls}
          />
        );
      }

      return (
        <AccessGroupResourcesPage
          key={AccessGroupResourceType.DnsRemote}
          title="IP address groups"
          addButtonLabel="Add IP address group"
          resourceType={AccessGroupResourceType.DnsRemote}
          filterRemap="filterDnsRemoteName"
          accountQuery={DNS_REMOTES_DOCUMENT}
          accountItemsPath="dnsRemotes"
          extraAccountVariables={{ partnerClientAccountId: [Number(accountId)] }}
          accountQueryVariables={getDnsListAccountQueryVariables(accountId)}
          getItemId={(item) => item.id}
          getItemPrimary={(item) => item.name || item.zone}
          getItemSecondary={(item) => item.address || item.id}
          innerTabs={[
            { key: resourceTabs.ipAddressGroups, title: 'IP address groups' },
            { key: resourceTabs.dnsZones, title: 'Dns zones' },
            { key: resourceTabs.acls, title: 'ACLs' },
          ]}
          defaultResourceTab={resourceTabs.ipAddressGroups}
        />
      );
    }

    return <EditAccessGroupGeneral accessGroup={accessGroup} loading={loading} refetch={refetch} />;
  };

  if (loading && !accessGroup) {
    return (
      <Box>
        <Typography variant="body1">Loading...</Typography>
      </Box>
    );
  }

  return renderContent();
};

export default EditAccessGroup;
